import { Box, Button, LoadingOverlay, Radio, ScrollArea, UnstyledButton } from '@mantine/core';
import { Modal } from '@/components/modal';
import { useStyles } from '../../../../styles';
import React from 'react';
import { useMediaQuery } from '@mantine/hooks';
import {
  EmailServerIntegrationType,
  IMailServer,
  TEMPLATE_MAIL_SERVER
} from '@/services/api/emailServerApiArea/types';
import MailServerIntegration from '@/containers/pages/filials/edit/components/tabs/mail-servers/components/mailServerIntegration';
import { useServerModalForm } from '@/containers/pages/filials/edit/components/tabs/mail-servers/components/useServerModalForm';
import { useTranslation } from 'react-i18next';
import MainForm from '@/containers/pages/filials/edit/components/tabs/mail-servers/components/mainForm';

interface ICreateMailServerProps {
  CustomerId: number;
  mailServer: IMailServer;
  opened: boolean;
  isEditing: boolean;

  handleClose(): void;
  handleChange?(): void;
}

export const MailServerForm = (props: ICreateMailServerProps) => {
  const { mailServer, opened, isEditing, handleClose } = props;
  const { classes, cx } = useStyles();

  const { form, setIsOnIntegration, handleSubmit, modalClose, isOnIntegration, isFetching } =
    useServerModalForm({ ...props });

  const isMobile = useMediaQuery('(max-width: 992px)');
  const isTablet = useMediaQuery('(max-width: 1400px)');
  const { t } = useTranslation();

  const handleSetSshType = () => {
    const currentIntegration = form?.values?.Integration || {};

    form.setFieldValue('Integration', {
      ...currentIntegration,
      Type: Number(EmailServerIntegrationType.Ssh),
      Ssh: structuredClone(mailServer?.Integration?.Ssh || TEMPLATE_MAIL_SERVER.TEMPLATE_SSH)
    });
  };

  const handleSetApiType = () => {
    const currentIntegration = form?.values?.Integration || {};

    form.setFieldValue('Integration', {
      ...currentIntegration,
      Type: Number(EmailServerIntegrationType.Api),
      Api: structuredClone(mailServer?.Integration?.Api || TEMPLATE_MAIL_SERVER.TEMPLATE_API)
    });
  };

  const toggleIntegration = () => {
    setIsOnIntegration((prev) => {
      const nextValue = !prev;
      if (nextValue) {
        handleSetSshType();
        return nextValue;
      }

      form.setFieldValue('Integration.Type', EmailServerIntegrationType.None);
      return nextValue;
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={modalClose}
      title={isEditing ? t('servers.edit') : t('servers.add')}
      zIndex={1300}
      scrollAreaComponent={ScrollArea.Autosize}
      size={isTablet ? '90%' : '60%'}
    >
      <form className={classes.modalRoot} onSubmit={form.onSubmit(handleSubmit)}>
        <Box className={classes.modalContainer} sx={{ paddingBottom: '100px' }}>
          <MainForm
            form={form}
            toggleIntegration={toggleIntegration}
            isOnIntegration={isOnIntegration}
            integrationType={form.values.Integration?.Type}
          />
          {isOnIntegration && (
            <Box pl={isMobile ? 0 : 34} w="100%">
              <Radio
                checked={form.values.Integration.Type === EmailServerIntegrationType.Ssh}
                className={classes.radio}
                mt={18}
                label="SSH"
                size="xs"
                data-cy="enableGeneratePassword"
                onChange={handleSetSshType}
              />
              <Radio
                checked={form.values.Integration.Type === EmailServerIntegrationType.Api}
                className={classes.radio}
                label="API"
                size="xs"
                data-cy="enableGeneratePassword"
                onChange={handleSetApiType}
              />
              <MailServerIntegration form={form} />
            </Box>
          )}
        </Box>
        <Box className={cx(classes.modalFooter, classes.customFooter)}>
          <Button type="submit" mb="0" mr={isMobile ? 0 : 24} w={isMobile ? '100%' : '49%'}>
            {isEditing ? t('save') : t('add')}
          </Button>
          <UnstyledButton
            ml={8}
            className={classes.modalCancelBtn}
            w={isMobile ? '100%' : '49%'}
            onClick={handleClose}
          >
            {t('cancel')}
          </UnstyledButton>
        </Box>
        <LoadingOverlay visible={isFetching} overlayProps={{ blur: 2 }} />
      </form>
    </Modal>
  );
};
